import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import { Job_Read, JobInvoicingUpdateChannel_Read } from '@treadinc/horizon-api-spec';
import { memo, useCallback, useContext, useEffect, useId } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Job, JobInvoicing } from '~hooks/useJob/models';
import { makeJobsQueryContextKey } from '~pages/Approvals/Files/utils';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobsTable from '../../JobsTable/JobsTable';
import JobsQueryContext from './JobsQueryContext';

export default memo(function JobsTab() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const instanceId = useId();

  const jobsQuery = useContext(JobsQueryContext);
  const queryClient = useQueryClient();

  // Fetch data on mount if visible
  useEffect(() => {
    jobsQuery.fetch();
  }, []);

  const handleJobRTU = useCallback(
    ({ data }: { data: Job_Read }) => {
      const parsedJob = Job.parse(data);
      const queryKey = makeJobsQueryContextKey(String(parsedJob.order?.id));

      queryClient.setQueryData<JobInvoicing[]>([queryKey], (oldJobs) => {
        if (oldJobs) {
          const oldJobIndex = oldJobs.findIndex((job) => job.id === parsedJob.id);

          if (oldJobIndex >= 0) {
            const oldJobInvoicing = oldJobs[oldJobIndex];
            const updatedJobInvoicing = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
              parsedJob,
              oldJobInvoicing.receivableInvoice,
              oldJobInvoicing.payableInvoice,
            );

            const newJobs = [...oldJobs];
            newJobs.splice(oldJobIndex, 1, updatedJobInvoicing);

            return newJobs;
          }
        }

        return oldJobs;
      });
    },
    [queryClient],
  );

  const handleJobInvoicingRTU = useCallback(
    (payload: JobInvoicingUpdateChannel_Read) => {
      const parsedJob = JobInvoicing.parse(payload.data);
      const queryKey = makeJobsQueryContextKey(String(parsedJob.order?.id));

      queryClient.setQueryData<JobInvoicing[]>([queryKey], (oldJobs) => {
        if (oldJobs) {
          const oldJobIndex = oldJobs.findIndex((job) => job.id === parsedJob.id);

          if (oldJobIndex >= 0) {
            const newJobs = [...oldJobs];
            newJobs.splice(oldJobIndex, 1, parsedJob);

            return newJobs;
          }

          return [...oldJobs, parsedJob];
        }

        return oldJobs;
      });
    },
    [queryClient],
  );

  useEffect(() => {
    const jobsKey = `${instanceId}_jobs`;
    const invoicingKey = `${instanceId}_invoicing`;

    subscribeWithMultipleHandlers(
      realTimeChannels.CompanyJobUpdateChannel,
      companyId,
      jobsKey,
      handleJobRTU,
    );

    subscribeWithMultipleHandlers(
      realTimeChannels.JobInvoicingUpdateChannel,
      companyId,
      invoicingKey,
      handleJobInvoicingRTU,
    );

    return () => {
      unsubscribeHandler(realTimeChannels.CompanyJobUpdateChannel, companyId, jobsKey);
      unsubscribeHandler(
        realTimeChannels.JobInvoicingUpdateChannel,
        companyId,
        invoicingKey,
      );
    };
  }, [companyId, instanceId, handleJobRTU, handleJobInvoicingRTU]);

  if (!jobsQuery.data) {
    return (
      <Box>
        <LoadingSpinner sx={{ my: 2 }} isVisible />
      </Box>
    );
  }

  return (
    <Box>
      <JobsTable jobs={jobsQuery.data} />
    </Box>
  );
});
